import { useCallback, useRef, useState } from 'react'

import { Flex } from '../../../../Components'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import {
  AddServiceButton,
  ServiceEmptyState,
  SingleServiceContainer,
  TransferField
} from '../Components'
import {
  addService,
  getElementsByService,
  getServiceCount,
  readKey,
  removeService
} from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { getDiffStatus } from './Functions'
import { SKIP_TRAIN_SEARCH_LOCATION, useMultiServiceZodValidation } from './multiServiceValidation'
import { buildTrainServiceSchema } from './Train.schema'
const language = cap.language.state || 'IT'
const t = translations[language]

const {
  train: {
    typeTrip,
    companyName,
    classType,
    seatPreference,
    departureDate,
    trainCityDeparture,
    fromHourDeparture,
    toHourDeparture,
    trainStationDeparture,
    trainStationDepartureDyn,
    arrivalDate,
    trainCityArrival,
    fromHourReturn,
    toHourReturn,
    trainStationArrival,
    trainStationArrivalDyn,
    withinHoursDeparture,
    carnet,
    noteAggiuntive = '' /*, withinHoursArrival */
  }
} = getElementsByService()

const SECTION_ID = 'train'

export const TrainSection = ({
  state: [state, setState],
  readOnly,
  diff,
  enableArrivoEntro = true
}) => {
  const [valTick, setValTick] = useState(0)
  const trackingSetState = (data, forceRender) => {
    setState(data, forceRender)
    setValTick((v) => v + 1)
  }
  const stateProp = [state, trackingSetState]
  const statePropRender = [state, (data) => trackingSetState(data, true)]
  const containerRef = useRef(null)
  const { sectionHasErrors } = useGigiFormValidation()

  const trainCount = getServiceCount(state, 'train')

  const buildSchema = useCallback(
    () => buildTrainServiceSchema({ enableArrivoEntro }),
    [enableArrivoEntro]
  )
  const buildServiceValues = useCallback(
    (formState, i) => ({
      typeTrip: readKey(formState, typeTrip.location, i) ?? '',
      departureDate: readKey(formState, departureDate.location, i) ?? '',
      trainCityDeparture: readKey(formState, trainCityDeparture.location, i) ?? '',
      trainCityArrival: readKey(formState, trainCityArrival.location, i) ?? '',
      fromHourDeparture: readKey(formState, fromHourDeparture.location, i) ?? '',
      toHourDeparture: readKey(formState, toHourDeparture.location, i) ?? '',
      trainStationDeparture: readKey(formState, trainStationDeparture.location, i) ?? '',
      trainStationDepartureDyn: readKey(formState, trainStationDepartureDyn.location, i) ?? '',
      arrivalDate: readKey(formState, arrivalDate.location, i) ?? '',
      fromHourReturn: readKey(formState, fromHourReturn.location, i) ?? '',
      trainStationArrival: readKey(formState, trainStationArrival.location, i) ?? '',
      trainStationArrivalDyn: readKey(formState, trainStationArrivalDyn.location, i) ?? '',
      toHourReturn: readKey(formState, toHourReturn.location, i) ?? '',
      companyName: readKey(formState, companyName.location, i) ?? '',
      classType: readKey(formState, classType.location, i) ?? '',
      seatPreference: readKey(formState, seatPreference.location, i) ?? '',
      noteAggiuntive: readKey(formState, noteAggiuntive.location, i) ?? '',
      withinHoursDeparture: readKey(formState, withinHoursDeparture.location, i) ?? '',
      skipTrainSearch: readKey(formState, SKIP_TRAIN_SEARCH_LOCATION, i) ?? '',
      carnet: readKey(formState, carnet.location, i) ?? ''
    }),
    []
  )

  useMultiServiceZodValidation({
    sectionId: SECTION_ID,
    containerRef,
    state,
    serviceCount: trainCount,
    buildSchema,
    buildServiceValues,
    trigger: valTick
  })

  const getProps = (stateRender, item, key, count, readOnly, diff) => ({
    state: stateRender ? statePropRender : stateProp,
    item,
    key,
    count,
    readOnly,
    diffStatus: getDiffStatus(item, diff, count)
  })

  return (
    <SectionShell
      id="train"
      icon="train"
      title={t.serviziTreno?.toUpperCase() || 'TRENO'}
      storageKey="gigiForm.section.train"
      hasErrors={sectionHasErrors('train')}
      count={trainCount}
      contentId="train-content"
      headerExtra={
        !readOnly && (
          <AddServiceButton
            label={t.aggiungiServizio}
            onClick={async () => setState(await addService(state, 'train'))}
          />
        )
      }
    >
      <div ref={containerRef}>
        <Flex fw>
          {!trainCount && (
            <ServiceEmptyState
              icon="train"
              title={t.nessunServizioTrenoInserito}
              hint={t.servizioEmptyHint}
            />
          )}
          {Array(getServiceCount(state, 'train'))
            .fill()
            .map((_, i) => {
              const departureDateValue = readKey(state, departureDate?.location, i)
              const minDateArrival = departureDateValue
                ? new Date(
                    new Date(
                      departureDateValue.split('/')[2],
                      departureDateValue.split('/')[1] - 1,
                      departureDateValue.split('/')[0]
                    )
                  )
                : new Date()

              const isWithinHoursDeparture = readKey(state, withinHoursDeparture?.location, i)
              const isRoundTrip = readKey(state, typeTrip?.location, i) === 'RT'
              const skipTrainSearch = readKey(
                state,
                'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.skipTrainSearch',
                i
              )
              const carnetValue = readKey(state, carnet?.location, i)
              const hasCarnet = carnetValue && Array.isArray(carnetValue) && carnetValue.length > 0
              return (
                <SingleServiceContainer
                  key={i}
                  index={i}
                  serviceType="train"
                  state={state}
                  onRemove={() => setState(removeService(state, 'train', i))}
                  readOnly={readOnly}
                >
                  {hasCarnet && (
                    <div
                      className="ff-service-grid--full"
                      style={{
                        marginBottom: 'var(--space-2)',
                        marginTop: 'var(--space-1)',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--color-success-bg)',
                        border: '1px solid var(--color-success)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-success)',
                        fontFamily: 'var(--font-family)'
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 'var(--font-weight-bold)',
                          fontSize: 'var(--font-size-md)',
                          marginBottom: 'var(--space-1)'
                        }}
                      >
                        🎫 {t.gfServizioCopertoDaCarnet}
                      </div>
                      {carnetValue.map((carnetItem, index) => (
                        <div
                          key={index}
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            marginLeft: 'var(--space-2)'
                          }}
                        >
                          • {carnetItem}
                        </div>
                      ))}
                    </div>
                  )}
                  <TransferField {...getProps(true, typeTrip, 'typeTrip', i, readOnly, diff)} />
                  <TransferField
                    {...getProps(false, companyName, 'companyName', i, readOnly, diff)}
                  />
                  <TransferField {...getProps(false, classType, 'classType', i, readOnly, diff)} />
                  <TransferField
                    {...getProps(false, seatPreference, 'seatPreference', i, readOnly, diff)}
                  />
                  {/* departure */}
                  <TransferField
                    {...getProps(true, departureDate, 'departureDate', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      trainCityDeparture,
                      'trainCityDeparture',
                      i,
                      readOnly,
                      diff
                    )}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      { ...fromHourDeparture, notRequired: isWithinHoursDeparture },
                      'fromHourDeparture',
                      i,
                      readOnly,
                      diff
                    )}
                  />
                  {!!enableArrivoEntro && (
                    <TransferField
                      {...getProps(false, toHourDeparture, 'toHourDeparture', i, readOnly, diff)}
                    />
                  )}
                  {/* arrival */}
                  <TransferField
                    {...getProps(
                      false,
                      !skipTrainSearch ? trainStationDepartureDyn : trainStationDeparture,
                      !skipTrainSearch ? 'trainStationDepartureDyn' : 'trainStationDeparture',
                      i,
                      readOnly,
                      diff
                    )}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      { ...arrivalDate, notRequired: !isRoundTrip, minDate: minDateArrival },
                      'arrivalDate',
                      i,
                      readOnly || !isRoundTrip,
                      diff
                    )}
                  />
                  <TransferField
                    {...getProps(false, trainCityArrival, 'trainCityArrival', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      { ...fromHourReturn, notRequired: !isRoundTrip },
                      'fromHourReturn',
                      i,
                      readOnly || !isRoundTrip,
                      diff
                    )}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      !skipTrainSearch ? trainStationArrivalDyn : trainStationArrival,
                      !skipTrainSearch ? 'trainStationArrivalDyn' : 'trainStationArrival',
                      i,
                      readOnly,
                      diff
                    )}
                  />
                  {!!enableArrivoEntro && (
                    <TransferField
                      {...getProps(false, toHourReturn, 'toHourReturn', i, readOnly, diff)}
                    />
                  )}
                  <div aria-hidden className="ff-service-grid--break" />
                  <div className="ff-service-grid--full">
                    <TransferField
                      {...getProps(false, noteAggiuntive, 'noteAggiuntive', i, readOnly, diff)}
                    />
                  </div>
                </SingleServiceContainer>
              )
            })}
        </Flex>
      </div>
    </SectionShell>
  )
}
